import { useMemo, useRef } from "react";
import { useTranslation } from "@helpers/translate";

import "./index.sass";

const SMS = ({
  title = "CMC20",
  value = "--",
  change = "--",
  isPositive,
  chartData = [],
  isLoading = false,
  width = 280,
  height = 60,
}) => {
  const { __ } = useTranslation();

  const idRef = useRef(`sms-${Math.random().toString(36).slice(2, 9)}`);
  const greenGradientId = `${idRef.current}-greenGradient`;
  const clipAboveId = `${idRef.current}-clip-above`;

  const chartConfig = useMemo(() => {
    const preparedChartData = chartData
      .map(Number)
      .filter((item) => Number.isFinite(item) && item > 0);

    const baselineY = height * 0.7;
    const padding = 0;

    if (preparedChartData.length < 2) {
      return {
        hasChartData: false,
        baselineY,
        areaPoints: "",
        segments: [],
      };
    }

    const maxValue = Math.max(...preparedChartData);
    const minValue = Math.min(...preparedChartData);
    const range = maxValue - minValue || 1;

    const points = preparedChartData.map((item, index) => {
      const x =
        padding +
        (index / (preparedChartData.length - 1)) * (width - padding * 2);

      const y =
        height - padding - ((item - minValue) / range) * (height - padding * 2);

      return { x, y };
    });

    const areaPoints = `${padding},${height} ${points
      .map((point) => `${point.x},${point.y}`)
      .join(" ")} ${width - padding},${height}`;

    const segments = [];

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      const p1Below = p1.y > baselineY;
      const p2Below = p2.y > baselineY;

      if (p1Below === p2Below) {
        segments.push({
          x1: p1.x,
          y1: p1.y,
          x2: p2.x,
          y2: p2.y,
          below: p1Below,
        });
      } else {
        const t = (baselineY - p1.y) / (p2.y - p1.y);
        const intersectionX = p1.x + t * (p2.x - p1.x);

        segments.push({
          x1: p1.x,
          y1: p1.y,
          x2: intersectionX,
          y2: baselineY,
          below: p1Below,
        });

        segments.push({
          x1: intersectionX,
          y1: baselineY,
          x2: p2.x,
          y2: p2.y,
          below: p2Below,
        });
      }
    }

    return {
      hasChartData: true,
      baselineY,
      areaPoints,
      segments,
    };
  }, [chartData, width, height]);

  return (
    <div className="sms-card">
      <div className="sms-card__header">
        <span className="sms-card__title">{title}</span>
      </div>

      <div className="sms-card__stats">
        <span className="sms-card__value">${isLoading ? "--" : value}</span>

        <div
          className={`sms-card__change ${
            isPositive === undefined ? "" : isPositive ? "positive" : "negative"
          }`}
        >
          <span>{`${isLoading ? "--" : change}(24${__(
            "common.hours.short",
          )})`}</span>

          {!isLoading && isPositive !== undefined && (
            <svg
              className="sms-card__trend-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              {isPositive ? (
                <path
                  d="M3 17L9 11L13 15L21 7M21 7H15M21 7V13"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M3 7L9 13L13 9L21 17M21 17H15M21 17V11"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          )}
        </div>
      </div>

      <div className="sms-card__chart">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          {!isLoading && chartConfig.hasChartData && (
            <>
              <defs>
                <linearGradient
                  id={greenGradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
                  <stop offset="50%" stopColor="#10b981" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                </linearGradient>

                <clipPath id={clipAboveId}>
                  <rect
                    x="0"
                    y="0"
                    width={width}
                    height={chartConfig.baselineY}
                  />
                </clipPath>
              </defs>

              <polygon
                points={chartConfig.areaPoints}
                fill={`url(#${greenGradientId})`}
                clipPath={`url(#${clipAboveId})`}
                className="sms-card__area--green"
              />

              <line
                x1="0"
                y1={chartConfig.baselineY}
                x2={width}
                y2={chartConfig.baselineY}
                className="sms-card__baseline"
              />

              {chartConfig.segments.map((segment, index) => (
                <line
                  key={index}
                  x1={segment.x1}
                  y1={segment.y1}
                  x2={segment.x2}
                  y2={segment.y2}
                  className={`sms-card__segment ${
                    segment.below ? "below" : "above"
                  }`}
                />
              ))}
            </>
          )}
        </svg>
      </div>
    </div>
  );
};

export default SMS;
